import { Component, ViewChild, ElementRef, OnInit, OnDestroy, Input, Output, EventEmitter, SimpleChanges, OnChanges, AfterViewInit } from '@angular/core';
import {NgClass, NgIf} from '@angular/common';
import { AnimationOptions } from 'ngx-lottie';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { Router } from '@angular/router';
import { EventLoggerService } from '@services/event-logger-service';

@Component({
  selector: 'app-gamification-scratch-card',
  imports: [
    NgIf,
    NgClass,
  ],
  templateUrl: './gamification-scratch-card.component.html',
})
export class GamificationScratchCardComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('scratchCanvas', { static: false }) scratchCanvas: ElementRef<HTMLCanvasElement>;
  @Input() rewardData: {
    objectId: string;
    amount?: number;
    type?: string;
    expireAt?: { iso?: string };
  };
  @Input() show: boolean = true;
  @Output() closed = new EventEmitter<void>();
  @Output() rewardClaimed = new EventEmitter<any>();

  options: AnimationOptions = {
    path: '/assets/animation.json',
  };
  styles: Partial<CSSStyleDeclaration> = {
    position: 'fixed',
    bottom: '0',
  };

  scratchCardData: {
    isScratched: boolean;
    isClaimed: boolean;
    rewardAmount?: number;
  } = {
    isScratched: false,
    isClaimed: false
  };

  get expiryDate(): string | null {
    if (!this.rewardData?.expireAt?.iso) return null;
    const date = new Date(this.rewardData.expireAt.iso);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }

  imageDataValue: number = 0;
  isScratching: boolean = false;
  isCanvasReady: boolean = false;

  handleTouchMove = (event: TouchEvent): void => {
    this.paintScratch(event.touches[0]);
  };

  handleMouseMove = (event: MouseEvent): void => {
    this.paintScratch(event);
  };

  constructor(
    private readonly conn: ConnectionService,
    private readonly broadcastService: BroadcastService,
    private readonly router: Router,
    private readonly eventLogger: EventLoggerService
    ) {}

  ngOnInit(): void {
    this.broadcastService.broadcast('HOMEPAGE_FULLSCREEN_MODE_ON', { show: true });
  }

  ngAfterViewInit(): void {
    if (this.show) {
      setTimeout(() => {
        this.prepareCanvas();
      }, 200);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['rewardData'] || changes['show']) && this.show && this.scratchCanvas?.nativeElement) {
      setTimeout(() => {
        this.prepareCanvas();
      }, 200);
    }
  }

  ngOnDestroy(): void {
    this.removeEventListeners();
  }

  prepareCanvas(): void {
    if (!this.scratchCanvas?.nativeElement) {
      console.warn('Canvas element not available');
      return;
    }

    this.isCanvasReady = false;
    this.imageDataValue = 0;
    this.scratchCardData.isScratched = false;
    const canvas = this.scratchCanvas.nativeElement;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Could not get 2d context');
      return;
    }

    const internalSize = 400;
    canvas.width = internalSize;
    canvas.height = internalSize;

    const grd = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    grd.addColorStop(0, 'rgba(56, 149, 129, 1)');
    grd.addColorStop(1, 'rgba(59, 183, 129, 1)');
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const rect = canvas.getBoundingClientRect();
    const displayWidth = rect.width || 160; // Updated for tw-w-40 (160px)
    const iconDisplaySize = 96; // Updated for tw-w-24 (96px)
    const iconCanvasSize = (iconDisplaySize / displayWidth) * canvas.width;

    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.onload = (): void => {
      const aspectRatio = image.width / image.height;
      const imgWidth = iconCanvasSize;
      const imgHeight = iconCanvasSize / aspectRatio;

      // Calculate text metrics first to determine total height
      const fontSize = (12 / displayWidth) * canvas.width; // 14px display size scaled to canvas
      ctx.font = `600 ${fontSize}px 'Noto Sans', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const textMetrics = ctx.measureText('Scratch here');
      const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;
      const textSpacing = (10 / displayWidth) * canvas.width; // 10px spacing on display

      // Calculate total height of icon + spacing + text
      const totalHeight = imgHeight + textSpacing + textHeight;

      // Center the combined icon + text vertically
      const combinedStartY = (canvas.height / 2) - (totalHeight / 2);

      const iconX = (canvas.width / 2) - imgWidth / 2;
      const iconY = combinedStartY;

      ctx.drawImage(
        image,
        iconX,
        iconY,
        imgWidth,
        imgHeight
      );

      // Draw "Scratch here" text below the gift icon
      ctx.fillStyle = 'white';
      const textY = iconY + imgHeight + textSpacing;
      ctx.fillText('Scratch here', canvas.width / 2, textY);

      const middlePortion = canvas.height / 3;
      const scratchImage = ctx.getImageData(
        0,
        middlePortion,
        canvas.width,
        middlePortion * 2
      );

      scratchImage.data.forEach((value: number): void => {
        this.imageDataValue += value;
      });
      this.addScratchEvents(canvas);
      this.isCanvasReady = true;
    };

    image.onerror = (): void => {
      this.addScratchEvents(canvas);
      this.isCanvasReady = true;
    };

    image.src = '/assets/icons/gift_translucent.svg';
  }

  addScratchEvents(canvas: HTMLCanvasElement | any): void {
    this.removeEventListeners();

    const storeCoordinates = (): void => {
      canvas.coordinates = canvas.getBoundingClientRect();
    };

    const touchStartHandler = (e: TouchEvent): void => {
      e.preventDefault();
      if (this.scratchCardData.isScratched) return;
      this.isScratching = true;
      storeCoordinates();
      canvas.addEventListener('touchmove', this.handleTouchMove);
    };

    const handleTouchEnd = (): void => {
      this.isScratching = false;
      canvas.removeEventListener('touchmove', this.handleTouchMove);
      this.checkIfScratched();
    };

    const mouseDownHandler = (e: MouseEvent): void => {
      if (this.scratchCardData.isScratched) return;
      this.isScratching = true;
      storeCoordinates();
      canvas.addEventListener('mousemove', this.handleMouseMove);
    };

    const handleMouseEnd = (): void => {
      this.isScratching = false;
      canvas.removeEventListener('mousemove', this.handleMouseMove);
      this.checkIfScratched();
    };

    canvas._touchStartHandler = touchStartHandler;
    canvas._touchEndHandler = handleTouchEnd;
    canvas._mouseDownHandler = mouseDownHandler;
    canvas._mouseEndHandler = handleMouseEnd;

    canvas.addEventListener('touchstart', touchStartHandler, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);
    canvas.addEventListener('touchcancel', handleTouchEnd);

    canvas.addEventListener('mousedown', mouseDownHandler);
    canvas.addEventListener('mouseup', handleMouseEnd);
    canvas.addEventListener('mouseleave', handleMouseEnd);
  }

  paintScratch(pointer: Touch | MouseEvent): void {
    if (!this.scratchCanvas?.nativeElement || this.scratchCardData.isScratched) {
      return;
    }

    const canvas = this.scratchCanvas.nativeElement as any;
    const coordinates = canvas.coordinates as DOMRect;

    if (!coordinates) {
      return;
    }

    const SCALE = canvas.width / coordinates.width;

    const x = (pointer.clientX - coordinates.left) * SCALE;
    const y = (pointer.clientY - coordinates.top) * SCALE;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 40, 0, 2 * Math.PI);
    ctx.fill();
    ctx.closePath();
  }

  async checkIfScratched(): Promise<void> {
    if (this.scratchCardData.isScratched || !this.scratchCanvas?.nativeElement) {
      return;
    }

    if (this.imageDataValue === 0) {
      return;
    }

    const canvas = this.scratchCanvas.nativeElement;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    const middlePortion = canvas.height / 3;
    const scratchImage = ctx.getImageData(
      0,
      middlePortion,
      canvas.width,
      middlePortion * 2
    );

    let valuesLeft = 0;
    scratchImage.data.forEach((value: number): void => {
      valuesLeft += value;
    });

    const percentageScratched = 100 - ((valuesLeft * 100) / this.imageDataValue);

    if (percentageScratched >= 50) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      this.scratchCardData.isScratched = true;

      // Log reward scratched event
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'skinStart_reward_scratched' }));

      if (this.rewardData?.objectId) {
        await this.claimReward();
      } else {
        this.scratchCardData.rewardAmount = this.rewardData?.amount;
      }
    }
  }

  async claimReward(): Promise<void> {
    if (!this.rewardData?.objectId) {
      return;
    }

    if (this.scratchCardData.isClaimed) {
      return;
    }

    try {
      await this.conn.claimReward(this.rewardData.objectId);

      this.scratchCardData.isClaimed = true;
      this.scratchCardData.rewardAmount = this.rewardData.amount;

      this.rewardClaimed.emit({
        objectId: this.rewardData.objectId,
        amount: this.rewardData.amount
      });

    } catch (error: any) {
      this.scratchCardData.isScratched = false;
      this.prepareCanvas();
    }
  }

  removeEventListeners(): void {
    if (!this.scratchCanvas?.nativeElement) return;

    const canvas = this.scratchCanvas.nativeElement as any;

    canvas.removeEventListener('touchmove', this.handleTouchMove);
    canvas.removeEventListener('mousemove', this.handleMouseMove);

    const touchStartHandler = canvas._touchStartHandler;
    const touchEndHandler = canvas._touchEndHandler;
    const mouseDownHandler = canvas._mouseDownHandler;
    const mouseEndHandler = canvas._mouseEndHandler;

    if (touchStartHandler) canvas.removeEventListener('touchstart', touchStartHandler);
    if (touchEndHandler) {
      canvas.removeEventListener('touchend', touchEndHandler);
      canvas.removeEventListener('touchcancel', touchEndHandler);
    }
    if (mouseDownHandler) canvas.removeEventListener('mousedown', mouseDownHandler);
    if (mouseEndHandler) {
      canvas.removeEventListener('mouseup', mouseEndHandler);
      canvas.removeEventListener('mouseleave', mouseEndHandler);
    }
  }

  closeScratchCard(): void {
    this.broadcastService.broadcast('HOMEPAGE_FULLSCREEN_MODE_OFF');
    this.scratchCardData.isClaimed = this.scratchCardData.isScratched;
    this.closed.emit();
    this.resetScratchCard();
  }

  shopNow(): void {
    this.closeScratchCard();
    const queryParams = { tab: 'shop' };
    this.router.navigate(['/shop'], { queryParams });
  }

  resetScratchCard(): void {
    this.scratchCardData = {
      isScratched: false,
      isClaimed: false
    };
    this.imageDataValue = 0;
    this.isScratching = false;
    this.isCanvasReady = false;
  }
}
